/**
 * この端末のプッシュ通知の状態（判定ロジックと localStorage の記録）
 *
 * 許可も購読も端末ごとのため、ここでの記録も端末ごとに持つ。サーバーの web_push_enabled は
 * ユーザー単位で、OFF にしても false に戻らないので、端末の状態の判定には使えない。
 */

/**
 * この端末でプッシュ通知を設定できるか
 * - available: 設定できる
 * - iosBrowser: iPhone・iPad のブラウザ。ホーム画面に追加したアプリからなら設定できる
 * - unsupported: 設定できない（古いブラウザ、アプリ内ブラウザなど）
 */
export type PushAvailability = 'available' | 'iosBrowser' | 'unsupported';

/** iPhone・iPad か。iPadOS の Safari は Mac の UA を名乗るので、タッチに対応した Mac も iPad とみなす */
export const isIosDevice = ({
  userAgent,
  maxTouchPoints,
}: {
  userAgent: string;
  maxTouchPoints: number;
}): boolean =>
  /iPad|iPhone|iPod/.test(userAgent) ||
  (/Macintosh/.test(userAgent) && maxTouchPoints > 1);

export const resolvePushAvailability = ({
  isSupported,
  userAgent,
  maxTouchPoints,
  isStandalone,
}: {
  isSupported: boolean;
  userAgent: string;
  maxTouchPoints: number;
  isStandalone: boolean;
}): PushAvailability => {
  if (isSupported) {
    return 'available';
  }
  // ホーム画面のアプリなのに対応していないのは iOS 16.4 より前。追加を案内しても設定できない
  if (isIosDevice({ userAgent, maxTouchPoints }) && !isStandalone) {
    return 'iosBrowser';
  }
  return 'unsupported';
};

/** この端末でプッシュ通知を ON にしたときの記録 */
export type PushDeviceRecord = {
  /** ON にしたユーザー。別のユーザーがログインしたときに、その人のトークンとして送らないために使う */
  userId: string;
  /** 最後にサーバーへ送ったトークン。トークンが変わったときに、前のトークンを置き換えるために使う */
  fcmToken: string;
  /** 最後にサーバーへ送った日時（ISO 8601） */
  syncedAt: string;
};

/** 前回の送り直しから、次の送り直しまでの間隔 */
export const TOKEN_REFRESH_INTERVAL_MS = 24 * 60 * 60 * 1000;

/**
 * 起動時にトークンを送り直すか（許可・購読の有無は呼び出し側で確かめる）
 * - refresh: 送り直す
 * - checkLegacy: 記録が無い。記録を残す前に ON にした端末かもしれないので、
 *   今のユーザーの web_push_enabled が true のときだけ送り直す
 * - skip: 送り直さない
 */
export const decideTokenRefresh = ({
  record,
  isOptedOut,
  userId,
  now,
}: {
  record: PushDeviceRecord | null;
  isOptedOut: boolean;
  userId: string;
  now: Date;
}): 'refresh' | 'checkLegacy' | 'skip' => {
  // 設定画面で OFF にした端末は、本人の意思に反して登録し直さない
  if (isOptedOut) {
    return 'skip';
  }
  if (!record) {
    return 'checkLegacy';
  }
  // 別のユーザーが ON にした端末。今のユーザーは ON にしていない
  if (record.userId !== userId) {
    return 'skip';
  }
  const syncedAt = new Date(record.syncedAt).getTime();
  if (
    !Number.isNaN(syncedAt) &&
    now.getTime() - syncedAt < TOKEN_REFRESH_INTERVAL_MS
  ) {
    return 'skip';
  }
  return 'refresh';
};

/**
 * 今のユーザーが、この端末でプッシュ通知を受け取れる状態か（設定画面のトグルの表示に使う）。
 * 記録が無い端末は、記録を残す前に ON にした可能性があるので、web_push_enabled で代える
 */
export const isEnabledOnDevice = ({
  availability,
  permission,
  hasSubscription,
  record,
  isOptedOut,
  userId,
  pushEnabledForUser,
}: {
  availability: PushAvailability;
  permission: NotificationPermission;
  hasSubscription: boolean;
  record: PushDeviceRecord | null;
  isOptedOut: boolean;
  userId: string;
  pushEnabledForUser: boolean;
}): boolean => {
  if (
    availability !== 'available' ||
    permission !== 'granted' ||
    !hasSubscription ||
    isOptedOut
  ) {
    return false;
  }
  return record ? record.userId === userId : pushEnabledForUser;
};

const RECORD_KEY = 'web-push-device';
const OPTED_OUT_KEY = 'web-push-opted-out';

const isPushDeviceRecord = (value: unknown): value is PushDeviceRecord =>
  typeof value === 'object' &&
  value !== null &&
  'userId' in value &&
  typeof value.userId === 'string' &&
  'fcmToken' in value &&
  typeof value.fcmToken === 'string' &&
  'syncedAt' in value &&
  typeof value.syncedAt === 'string';

// プライベートブラウズや一部の WebView では localStorage へのアクセス自体が例外を投げる。
// 記録できなくても通知の設定は続けられるので、読めなければ「記録なし」として扱う
export const readPushDeviceRecord = (): PushDeviceRecord | null => {
  try {
    const raw = localStorage.getItem(RECORD_KEY);
    if (raw === null) return null;
    const value: unknown = JSON.parse(raw);
    return isPushDeviceRecord(value) ? value : null;
  } catch {
    return null;
  }
};

export const writePushDeviceRecord = (record: PushDeviceRecord) => {
  try {
    localStorage.setItem(RECORD_KEY, JSON.stringify(record));
  } catch {
    // 記録できないだけなので何もしない
  }
};

export const readIsOptedOut = (): boolean => {
  try {
    return localStorage.getItem(OPTED_OUT_KEY) === 'true';
  } catch {
    return false;
  }
};

/** ON にしたときの記録を残し、OFF にした記録を消す */
export const rememberEnabled = (record: PushDeviceRecord) => {
  writePushDeviceRecord(record);
  try {
    localStorage.removeItem(OPTED_OUT_KEY);
  } catch {
    // 記録できないだけなので何もしない
  }
};

/** OFF にしたことを覚え、ON にしたときの記録を消す */
export const rememberOptedOut = () => {
  try {
    localStorage.setItem(OPTED_OUT_KEY, 'true');
    localStorage.removeItem(RECORD_KEY);
  } catch {
    // 記録できないだけなので何もしない
  }
};
